import { useMutation, useQueryClient } from '@tanstack/react-query';
import { basisPointsFromPercent, formatMoney, moneyFromCents, WAIVER_REASONS } from '@wodi/domain';
import type { ChargeView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  MoneyField,
  Notice,
  RadioGroup,
  Select,
  TextArea,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { billingKeys, requestAdjustment } from '../api/billing.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './billing.module.css';
import { cents, money } from './format.ts';

export const WAIVER_REASON_LABELS: Record<(typeof WAIVER_REASONS)[number], string> = {
  hardship: 'Hardship',
  staff: 'Staff',
  management_decision: 'Management decision',
  service_complaint: 'Service complaint',
  other: 'Other',
};

/**
 * Ask for a waiver or discount on the patient's share of one charge (CLAUDE.md, domain
 * rule 10). Someone else with approval rights decides; nothing changes until they approve.
 */
export function WaiverDialog({ charge, onClose }: { charge: ChargeView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'waiver' | 'discount'>('waiver');
  const [basis, setBasis] = useState<'amount' | 'percent'>('amount');
  const [amount, setAmount] = useState<bigint | null>(null);
  const [percent, setPercent] = useState('');
  const [reason, setReason] = useState<(typeof WAIVER_REASONS)[number] | null>(null);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);

  const rate = basisPointsFromPercent(percent);
  const due = cents(charge.due);
  const valueOk =
    basis === 'amount'
      ? amount !== null && amount > 0n && amount <= due
      : rate.ok && rate.value > 0 && rate.value <= 10_000;
  const valid = valueOk && reason !== null && (reason !== 'other' || note.trim() !== '');

  const submit = useMutation({
    mutationFn: () =>
      requestAdjustment(charge.id, {
        kind,
        basis,
        ...(basis === 'amount'
          ? { amountCents: (amount ?? 0n).toString() }
          : { rateBasisPoints: rate.ok ? rate.value : 0 }),
        reasonCode: reason ?? 'other',
        ...(note.trim() === '' ? {} : { reasonNote: note.trim() }),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({
        title: kind === 'waiver' ? 'Waiver requested' : 'Discount requested',
        description: `${money(result.amount)} waiting for approval`,
      });
      onClose();
    },
  });

  return (
    <Dialog
      title="Ask for a waiver or discount"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>
          {`${charge.itemName}. The patient pays ${formatMoney(moneyFromCents(due))}. Someone else with approval rights must approve it before the bill changes.`}
        </p>
        <RadioGroup
          label="What is it?"
          options={[
            { id: 'waiver', label: 'Waiver' },
            { id: 'discount', label: 'Discount' },
          ]}
          value={kind}
          onChange={(v) => {
            setKind(v as 'waiver' | 'discount');
          }}
        />
        <RadioGroup
          label="Take off"
          options={[
            { id: 'amount', label: 'An amount' },
            { id: 'percent', label: 'A percentage' },
          ]}
          value={basis}
          onChange={(v) => {
            setBasis(v as 'amount' | 'percent');
          }}
        />
        {basis === 'amount' ? (
          <MoneyField
            label="Amount to take off"
            value={amount}
            onChange={(value) => {
              setAmount(value);
            }}
            {...(tried && !valueOk
              ? { errorMessage: `Enter an amount up to ${formatMoney(moneyFromCents(due))}.` }
              : {})}
          />
        ) : (
          <TextField
            label="Percentage to take off"
            description="Of what the patient pays, for example 20 or 12.5."
            inputMode="decimal"
            value={percent}
            onChange={setPercent}
            {...(tried && !valueOk ? { errorMessage: 'Enter a percentage from 0.01 to 100.' } : {})}
          />
        )}
        <Select
          label="Reason"
          placeholder="Choose a reason"
          options={WAIVER_REASONS.map((r) => ({ id: r, label: WAIVER_REASON_LABELS[r] }))}
          value={reason}
          onChange={(v) => {
            setReason(v as (typeof WAIVER_REASONS)[number]);
          }}
          {...(tried && reason === null ? { errorMessage: 'Choose a reason.' } : {})}
        />
        <TextArea
          label="Note"
          description={reason === 'other' ? 'Required when the reason is other.' : 'Optional.'}
          value={note}
          onChange={setNote}
          {...(tried && reason === 'other' && note.trim() === ''
            ? { errorMessage: 'Say why.' }
            : {})}
        />
        {submit.isError ? (
          <Notice tone="error">
            {errorText(submit.error, "The request wasn't sent. Try again.")}
          </Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={submit.isPending}
          pendingLabel="Asking"
          onPress={() => {
            setTried(true);
            if (valid) submit.mutate();
          }}
        >
          Ask for approval
        </Button>
      </DialogActions>
    </Dialog>
  );
}
